import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'gatsby';

import { alphabetize, getLinkPath } from '../../utils/helpers';

import Poster from '../Poster';

import './MediaPanel.scss';

import { IMedia } from '../../utils/models';
export interface IMediaPanelProps {
  title?: string;
  altTitle?: string;
  items: IMedia[];
  networkName?: string;
  sortFn?: (items) => any[];
  pageSize?: number;
  children?: any;
  showTitle: boolean;
}

const MediaPanel = ({
  title,
  items,
  altTitle,
  networkName = '',
  sortFn,
  pageSize = 100,
  children,
  showTitle,
}: IMediaPanelProps) => {
  const [page, setPage] = useState<number>(0);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [isKeyboardUser, setIsKeyboardUser] = useState<boolean>(false);
  const [lastItemIndex, setLastItemIndex] = useState<number>(pageSize);
  const defaultTitle =
    title === 'All' ? `All (${items.length})` : `${title} (${items.length})`;

  const displayTitle = altTitle || defaultTitle;

  const sort = sortFn || useCallback((list) => alphabetize<IMedia>(list, 'title', 'asc'), []);

  const titles = useMemo(() => sort(items), [items, sortFn]);

  const [titlesInWindow, setTitlesInWindow] = useState(
    titles.slice(0, pageSize)
  );

  // Updates the page
  const loadMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  useEffect(() => {
    setTitlesInWindow(titles.slice(0, pageSize));
  }, [titles]);

  // Loads in the next slice of content
  useEffect(() => {
    const nextPageSlice = titles.slice(pageSize * page, pageSize * (page + 1));
    if (page > 0) {
      setTitlesInWindow((prevTitles) => [...prevTitles, ...nextPageSlice]);
    }
  }, [page, pageSize, items, titles]);

  // Handles setting keyboard focus on new item when pressing enter on the load more button
  useEffect(() => {
    if (isKeyboardUser) {
      const itemToFocus = listRef.current?.children[lastItemIndex + 1];
      if (itemToFocus) {
        (itemToFocus.children[0] as HTMLElement).focus();
      }
    }

    setLastItemIndex(titlesInWindow.length - 1);
  }, [titlesInWindow, isKeyboardUser, lastItemIndex]);

  const shouldShowLoadMoreButton = (page + 1) * pageSize <= titles.length;
  const nextPageSlice = titles.slice(pageSize * page, pageSize * (page + 1));
  const loadMoreAriaLabel = `Load ${
    pageSize <= nextPageSlice.length ? pageSize : nextPageSlice.length
  } more titles`;

  return (
    <div className="media-panel">
      {showTitle ? <h2 className="media-panel__title">{displayTitle}</h2> : ''}
      <ul
        className="media-panel__list"
        ref={listRef}
        aria-label={`Showing ${titlesInWindow.length} of ${titles.length} items.`}
      >
        {children
          ? children
          : titlesInWindow.map((result) => {
              const { title: mediaTitle } = result;
              // including index on key for when there are duplicate titles
              return (
                <li
                  className="media-panel__list-item"
                  key={`${result.id} ${result.id}`}
                >
                  <Link
                    to={result.path || getLinkPath(result, networkName)}
                    aria-label={`${mediaTitle}`}
                  >
                    <Poster media={result} />
                  </Link>
                </li>
              );
            })}
      </ul>

      {/* Load More Button */}
      {shouldShowLoadMoreButton ? (
        <button
          onClick={() => {
            setIsKeyboardUser(false);
            loadMore();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsKeyboardUser(true);
              loadMore();
            }
          }}
          className="button-link button-link--secondary"
          aria-label={loadMoreAriaLabel}
        >
          Load More
        </button>
      ) : null}
    </div>
  );
};

export default MediaPanel;
